import { FormControlLabel, Radio } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import React from 'react';

import { unoTheme } from '../../../../../../assets/styles/unoTheme';
import MyTextField from '../../../../../utils/MyTextField';
import UnoButton from '../../../../../utils/UnoButton';
import { isValidCnpj } from '../../../../../utils/utils';
import { isBlankInstitution } from './institutionUtils';

//Altura da linha reservada para o helperText (2px de margem + 14px de linha). Os campos usam
//`reservehelper`, então essa faixa existe com ou sem erro; o radio e o botão de excluir, que não
//têm helper, recebem o mesmo valor como padding para continuarem alinhados com os inputs.
const HELPER_RESERVED_HEIGHT = '16px';

const useStyles = makeStyles({
    row: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        marginBottom: '4px',

        //Normaliza a altura do helper para o valor acima ser previsível: por padrão o MUI usa
        //margin 8px + line-height 1.66, que varia com a fonte
        '& .MuiFormHelperText-root': {
            margin: '2px 0px 0px 0px',
            fontSize: '11px',
            lineHeight: '14px',
        },
    },
    //Larguras fixas nos campos de formato conhecido; a razão social fica com o que sobrar
    mainField: {
        flex: '0 0 auto',
        whiteSpace: 'nowrap',
        paddingBottom: HELPER_RESERVED_HEIGHT,
    },
    cnpjField: {
        flex: '0 0 180px',
    },
    nameField: {
        flex: 1,
        minWidth: '160px',
    },
    accountField: {
        flex: '0 0 150px',
    },
    actionField: {
        flex: '0 0 auto',
        paddingBottom: HELPER_RESERVED_HEIGHT,
    },
    mainLabel: {
        '& .MuiFormControlLabel-label': {
            fontSize: '12px',
            color: unoTheme.secondaryColor,
        },
    },
});

/**
 * Uma linha de custodiante ou liquidante na etapa 1.
 *
 * Layout em flex, e não em tabela: os campos têm formatos bem diferentes (radio, CNPJ mascarado,
 * razão social livre, conta B3) e a tabela obrigava a alinhar tudo em colunas rígidas.
 *
 * A ordem dos campos segue o subtítulo da seção — CNPJ, razão social e conta B3.
 *
 * @param {{
 *   item: {name: string, cnpj: string, account_number: string, is_main: boolean},
 *   singular: string,
 *   canEdit: boolean,
 *   submitted: boolean,
 *   withAccount: boolean,
 *   onChangeField: (field: string, value: string) => void,
 *   onSetMain: () => void,
 *   onRemove: () => void,
 * }} props
 */
export default function TrademateInstitutionRow(props) {

    const {
        item, singular, canEdit, submitted, withAccount,
        onChangeField, onSetMain, onRemove,
    } = props;

    const classes = useStyles();

    //Linha vazia não é acusada: ela é descartada no save
    const showErrors = submitted && !isBlankInstitution(item);

    return (
        <div className={classes.row}>

            <div className={classes.mainField}>
                <FormControlLabel
                    className={classes.mainLabel}
                    label={'Principal'}
                    control={
                        <Radio
                            checked={item.is_main === true}
                            disabled={!canEdit}
                            color="primary"
                            size="small"
                            onChange={onSetMain}
                        />
                    }
                />
            </div>

            <div className={classes.cnpjField}>
                <MyTextField
                    fullWidth
                    reservehelper
                    variant="outlined"
                    disabled={!canEdit}
                    mask={['cnpj']}
                    value={item.cnpj}
                    placeholder={'00.000.000/0000-00'}
                    validator={showErrors && !isValidCnpj(item.cnpj) ? true : undefined}
                    helperText={'Insira um CNPJ válido'}
                    onChange={(value) => onChangeField('cnpj', value)}
                />
            </div>

            <div className={classes.nameField}>
                <MyTextField
                    fullWidth
                    reservehelper
                    variant="outlined"
                    disabled={!canEdit}
                    value={item.name}
                    placeholder={'Razão Social do ' + singular.toLowerCase()}
                    validator={showErrors && !item.name ? true : undefined}
                    helperText={'Informe a razão social'}
                    onChange={(value) => onChangeField('name', value)}
                />
            </div>

            {withAccount && (
                <div className={classes.accountField}>
                    <MyTextField
                        fullWidth
                        reservehelper
                        variant="outlined"
                        disabled={!canEdit}
                        value={item.account_number}
                        placeholder={'Conta B3'}
                        validator={showErrors && !item.account_number ? true : undefined}
                        helperText={'Informe a conta B3'}
                        onChange={(value) => onChangeField('account_number', value)}
                    />
                </div>
            )}

            {canEdit && (
                <div className={classes.actionField}>
                    <UnoButton
                        isIconButton
                        removeDefaultLeftMargin
                        size='verySmall'
                        type='errorClean'
                        myIcon={<DeleteIcon />}
                        onClick={onRemove}
                    />
                </div>
            )}

        </div>
    );
}
